import { Query } from '../types/Query';
import { hasData } from './hasData';
import { toArray } from './toArray';

interface Options {
  skipNoData?: boolean;
  skipError?: boolean;
}

/**
 * Determines if a query (or array of queries) is not ready to be used.
 *
 * A query is considered "not ready" if any of the following conditions are true:
 * - Any query has an error (unless `skipError` is set)
 * - Any query is loading or fetching
 * - Any query has no data (unless `skipNoData` is set)
 *
 * @param query - A single query or an array of queries to check.
 * @param options - Optional configuration to modify the readiness check behavior.
 * @param options.skipNoData - If `true`, the check will not consider missing data as "not ready".
 *   Useful when you want to proceed even if the query returned no results.
 * @param options.skipError - If `true`, queries with errors will be considered "ready" and
 *   the function returns `false` immediately when an error is detected. Useful when you want
 *   to handle errors separately and not block rendering.
 * @returns `true` if the query is not ready, `false` if it is ready to use.
 */
export function isNotReady(query: Query | Query[], options?: Options): boolean {
  const queries: Query[] = toArray(query);

  const isError = queries.some((response) => response.isError);

  // Check skipError + isError FIRST - don't wait for retries if we're skipping errors.
  // If we wait, it causes an infinite loop when child components subscribe to the same query.
  if (options?.skipError && isError) {
    return false;
  }

  // Then check loading/fetching.
  if (queries.some((request) => request.isLoading || request.isFetching)) {
    return true;
  }

  // Check error without skipError.
  if (isError) {
    return true;
  }

  if (
    !options?.skipNoData &&
    queries.some((response) => !hasData(response.data))
  ) {
    return true;
  }

  return false;
}
